import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react';

export const emptyQuote = () => ({
    number: null,
    offer_date: new Date().toISOString().slice(0, 10),
    revision_date: '',
    revision_note: '',
    vehicles_count: 1,
    vehicle: '',
    vehicle_type: '',
    floors: '',
    length: '',
    vehicle_price: '',
    vehicle_note: '',
    optionals: [],
    list_price: '',
    max_discount_price: '',
    notes: '',
});

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

export const computeQuote = (q) => {
    const optionalsTotal = (q.optionals || []).reduce((s, o) => s + num(o.price), 0);
    const unitTotal = num(q.vehicle_price) + optionalsTotal;
    const count = Math.max(1, parseInt(q.vehicles_count, 10) || 1);
    const total = unitTotal * count;
    const listPrice = num(q.list_price);
    const maxDiscount = num(q.max_discount_price);
    const discountPct = listPrice > 0 ? (1 - unitTotal / listPrice) * 100 : null;
    const belowMin = maxDiscount > 0 && unitTotal < maxDiscount;
    return { optionalsTotal, unitTotal, count, total, discountPct, belowMin, listPrice, maxDiscount };
};

export const serializeQuote = (q) => ({
    ...q,
    vehicles_count: Math.max(1, parseInt(q.vehicles_count, 10) || 1),
    floors: q.floors === '' || q.floors === null ? null : parseInt(q.floors, 10),
    length: q.length === '' || q.length === null ? null : parseFloat(q.length),
    vehicle_price: num(q.vehicle_price),
    list_price: q.list_price === '' || q.list_price === null ? null : num(q.list_price),
    max_discount_price: q.max_discount_price === '' || q.max_discount_price === null ? null : num(q.max_discount_price),
    revision_date: q.revision_date || null,
    revision_note: q.revision_note || null,
    vehicle_note: q.vehicle_note || null,
    notes: q.notes || null,
    optionals: (q.optionals || []).filter(o => o.description || o.price !== '').map(o => ({ description: o.description, price: num(o.price) })),
});

export const fmtEur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

const Field = ({ label, children, className = '' }) => (
    <div className={className}>
        <Label className="text-xs uppercase tracking-wider">{label}</Label>
        {children}
    </div>
);

export const QuoteForm = ({ quote, onChange, sellerName, clientName }) => {
    const set = (patch) => onChange({ ...quote, ...patch });
    const t = computeQuote(quote);

    const setOptional = (i, patch) => {
        const optionals = quote.optionals.map((o, idx) => (idx === i ? { ...o, ...patch } : o));
        set({ optionals });
    };
    const addOptional = () => set({ optionals: [...quote.optionals, { description: '', price: '' }] });
    const removeOptional = (i) => set({ optionals: quote.optionals.filter((_, idx) => idx !== i) });

    return (
        <div className="space-y-5" data-testid="quote-form">
            <div className="flex items-center justify-between rounded-sm border bg-muted/40 px-4 py-3">
                <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Numero preventivo</p>
                    <p className="font-heading text-lg" data-testid="quote-number">
                        {quote.number || 'Assegnato al salvataggio'}
                    </p>
                </div>
                <div className="text-right text-sm">
                    <p><span className="text-muted-foreground">Venditore:</span> {sellerName || '—'}</p>
                    <p><span className="text-muted-foreground">Cliente:</span> {clientName || '—'}</p>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Data offerta">
                    <Input type="date" value={quote.offer_date || ''} onChange={(e) => set({ offer_date: e.target.value })} data-testid="quote-offer-date" />
                </Field>
                <Field label="Data modifica">
                    <Input type="date" value={quote.revision_date || ''} onChange={(e) => set({ revision_date: e.target.value })} data-testid="quote-revision-date" />
                </Field>
                <Field label="Nota modifica">
                    <Input placeholder="es. modificato dopo incontro" value={quote.revision_note || ''} onChange={(e) => set({ revision_note: e.target.value })} data-testid="quote-revision-note" />
                </Field>
                <Field label="Numero veicoli">
                    <Input type="number" min="1" value={quote.vehicles_count} onChange={(e) => set({ vehicles_count: e.target.value })} data-testid="quote-vehicles-count" />
                </Field>
            </div>

            <div className="rounded-sm border p-4 space-y-3">
                <h4 className="font-heading uppercase text-sm tracking-wider">Veicolo</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <Field label="Veicolo">
                        <Input placeholder="es. Motrice" value={quote.vehicle} onChange={(e) => set({ vehicle: e.target.value })} data-testid="quote-vehicle" />
                    </Field>
                    <Field label="Tipologia">
                        <Select value={quote.vehicle_type || undefined} onValueChange={(v) => set({ vehicle_type: v })}>
                            <SelectTrigger data-testid="quote-vehicle-type"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Standard">Standard</SelectItem>
                                <SelectItem value="Gran Volume">Gran Volume</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Prezzo veicolo (€)">
                        <Input type="number" min="0" value={quote.vehicle_price} onChange={(e) => set({ vehicle_price: e.target.value })} data-testid="quote-vehicle-price" />
                    </Field>
                    <Field label="Numero piani">
                        <Input type="number" min="0" value={quote.floors} onChange={(e) => set({ floors: e.target.value })} data-testid="quote-floors" />
                    </Field>
                    <Field label="Lunghezza (m)">
                        <Input type="number" step="0.1" min="0" value={quote.length} onChange={(e) => set({ length: e.target.value })} data-testid="quote-length" />
                    </Field>
                    <Field label="Nota veicolo">
                        <Input placeholder="es. VENDUTO / cambio tipologia" value={quote.vehicle_note || ''} onChange={(e) => set({ vehicle_note: e.target.value })} data-testid="quote-vehicle-note" />
                    </Field>
                </div>
            </div>

            <div className="rounded-sm border p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <h4 className="font-heading uppercase text-sm tracking-wider">Optional</h4>
                    <Button type="button" size="sm" variant="outline" onClick={addOptional} data-testid="quote-add-optional">
                        <Plus size={14} className="mr-1" /> Aggiungi
                    </Button>
                </div>
                {quote.optionals.length === 0 && (
                    <p className="text-sm text-muted-foreground">Nessun optional. Aggiungi telecomando, tetto idraulico, carenature…</p>
                )}
                {quote.optionals.map((o, i) => (
                    <div key={i} className="flex gap-2 items-center" data-testid={`quote-optional-row-${i}`}>
                        <Input
                            className="flex-1"
                            placeholder="Descrizione optional"
                            value={o.description}
                            onChange={(e) => setOptional(i, { description: e.target.value })}
                            data-testid={`quote-optional-desc-${i}`}
                        />
                        <Input
                            className="w-32"
                            type="number"
                            placeholder="€ 0"
                            value={o.price}
                            onChange={(e) => setOptional(i, { price: e.target.value })}
                            data-testid={`quote-optional-price-${i}`}
                        />
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeOptional(i)} data-testid={`quote-optional-remove-${i}`}>
                            <Trash2 size={14} />
                        </Button>
                    </div>
                ))}
                {quote.optionals.length > 0 && (
                    <p className="text-right text-sm font-mono text-muted-foreground">Somma optional: {fmtEur(t.optionalsTotal)}</p>
                )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-sm border p-4 space-y-2 bg-muted/30">
                    <div className="flex justify-between text-sm">
                        <span>Prezzo veicolo</span><span className="font-mono">{fmtEur(num(quote.vehicle_price))}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span>+ Optional</span><span className="font-mono">{fmtEur(t.optionalsTotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t pt-2">
                        <span>Totale per veicolo</span><span className="font-mono" data-testid="quote-unit-total">{fmtEur(t.unitTotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span>× {t.count} veicol{t.count === 1 ? 'o' : 'i'}</span><span />
                    </div>
                    <div className="flex justify-between items-center border-t pt-2">
                        <span className="font-heading uppercase tracking-wider">Totale offerta</span>
                        <span className="font-heading text-xl" data-testid="quote-total">{fmtEur(t.total)}</span>
                    </div>
                </div>

                <div className="rounded-sm border p-4 space-y-3">
                    <h4 className="font-heading uppercase text-sm tracking-wider">Verifica prezzo finale</h4>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Totale listino (€)">
                            <Input type="number" min="0" value={quote.list_price} onChange={(e) => set({ list_price: e.target.value })} data-testid="quote-list-price" />
                        </Field>
                        <Field label="Sconto massimo (€)">
                            <Input type="number" min="0" value={quote.max_discount_price} onChange={(e) => set({ max_discount_price: e.target.value })} data-testid="quote-max-discount" />
                        </Field>
                    </div>
                    {t.discountPct !== null && (
                        <p className="text-sm" data-testid="quote-discount-pct">
                            {t.discountPct >= 0 ? 'Sconto applicato sul listino: ' : 'Prezzo sopra listino: +'}
                            <span className="font-mono font-semibold">{Math.abs(t.discountPct).toFixed(1)}%</span>
                        </p>
                    )}
                    {t.maxDiscount > 0 && (
                        t.belowMin ? (
                            <div className="flex items-center gap-2 rounded-sm bg-red-50 text-red-700 border border-red-200 px-3 py-2 text-sm" data-testid="quote-below-min-alert">
                                <AlertTriangle size={16} />
                                Prezzo unitario {fmtEur(t.unitTotal)} sotto lo sconto massimo ({fmtEur(t.maxDiscount)})
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-2 text-sm" data-testid="quote-ok-alert">
                                <CheckCircle2 size={16} />
                                Prezzo entro lo sconto massimo (margine {fmtEur(t.unitTotal - t.maxDiscount)})
                            </div>
                        )
                    )}
                </div>
            </div>

            <Field label="Note preventivo">
                <Textarea rows={2} value={quote.notes || ''} onChange={(e) => set({ notes: e.target.value })} data-testid="quote-notes" />
            </Field>
        </div>
    );
};
